import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStores } from '../../../stores';
import {
  Card,
  List,
  Button,
  Modal,
  Form,
  DatePicker,
  Input,
  message,
  Spin,
  Tag,
  Empty,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './Services.scss';

const ServiceHolidays = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { partnerServiceStore } = useStores();
  const { currentService, loading } = partnerServiceStore;

  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    partnerServiceStore.fetchServiceById(id);
  }, [id, partnerServiceStore]);

  const handleAddHoliday = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleDeleteHoliday = async (holidayId) => {
    const result = await partnerServiceStore.deleteHoliday(id, holidayId);
    if (result.success) {
      message.success(t('partner.holidays.messages.deleteSuccess'));
      // Refresh service data to show updated holidays list
      await partnerServiceStore.fetchServiceById(id);
    } else {
      message.error(result.error);
    }
  };

  const handleSubmit = async (values) => {
    const data = {
      date: values.date.format('YYYY-MM-DD'),
      description: values.description || ''
    };

    const result = await partnerServiceStore.addHoliday(id, data);

    if (result.success) {
      message.success(t('partner.holidays.messages.addSuccess'));
      setModalVisible(false);
      form.resetFields();
      // Refresh service data to show updated holidays list
      await partnerServiceStore.fetchServiceById(id);
    } else {
      message.error(result.error);
    }
  };

  // Disable past dates
  const disabledDate = (current) => {
    return current && current < dayjs().startOf('day');
  };

  // Separate holidays into future and past
  const holidays = currentService?.holidays || [];
  const futureHolidays = holidays.filter(h => dayjs(h.date).isAfter(dayjs(), 'day') || dayjs(h.date).isSame(dayjs(), 'day'));
  const pastHolidays = holidays.filter(h => dayjs(h.date).isBefore(dayjs(), 'day'));

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="service-holidays">
      <div className="page-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/partner/services')}
        >
          {t('partner.services.backToServices')}
        </Button>
        <h1>{t('partner.holidays.manageHolidays')}</h1>
      </div>

      {currentService && (
        <Card className="service-info-card">
          <h2>{currentService.name_en}</h2>
          <p>{currentService.name_el}</p>
          <Tag color="blue">{currentService.service_category_name}</Tag>
        </Card>
      )}

      <Card
        title={t('partner.holidays.upcomingHolidays')}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddHoliday}
          >
            {t('partner.holidays.addHoliday')}
          </Button>
        }
      >
        {futureHolidays.length > 0 ? (
          <List
            dataSource={futureHolidays}
            renderItem={(holiday) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title={t('partner.holidays.deleteTitle')}
                    description={t('partner.holidays.deleteConfirm')}
                    onConfirm={() => handleDeleteHoliday(holiday.id)}
                    okText={t('common.delete')}
                    okType="danger"
                    cancelText={t('common.cancel')}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />}>
                      {t('common.delete')}
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  avatar={<CalendarOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                  title={
                    <span>
                      <Tag color="green">{t('partner.holidays.upcoming')}</Tag>
                      {dayjs(holiday.date).format('MMMM D, YYYY')} ({dayjs(holiday.date).format('dddd')})
                    </span>
                  }
                  description={holiday.description || t('partner.holidays.noDescription')}
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty
            description={t('partner.holidays.noUpcoming')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddHoliday}>
              {t('partner.holidays.addFirst')}
            </Button>
          </Empty>
        )}
      </Card>

      {pastHolidays.length > 0 && (
        <Card title={t('partner.holidays.pastHolidays')} className="past-holidays-card">
          <List
            dataSource={pastHolidays}
            renderItem={(holiday) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<CalendarOutlined style={{ fontSize: 24, color: '#999' }} />}
                  title={
                    <span style={{ color: '#999' }}>
                      <Tag>{t('partner.holidays.past')}</Tag>
                      {dayjs(holiday.date).format('MMMM D, YYYY')} ({dayjs(holiday.date).format('dddd')})
                    </span>
                  }
                  description={holiday.description || t('partner.holidays.noDescription')}
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      <Modal
        title={t('partner.holidays.addHoliday')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="date"
            label={t('partner.holidays.date')}
            rules={[{ required: true, message: t('partner.holidays.dateRequired') }]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              disabledDate={disabledDate}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('partner.holidays.descriptionOptional')}
          >
            <Input placeholder={t('partner.holidays.descriptionPlaceholder')} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {t('partner.holidays.addHoliday')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export default ServiceHolidays;
